import { tz } from "@date-fns/tz";
import { addMonths, startOfDay } from "date-fns";
import { z } from "zod";

const UTC = tz("UTC");

import { DistributionMethod } from "@/api/admin/fundings";

export const MAX_CREDIT_AMOUNT = 9_999_999;

const emptyToUndefined = (val: unknown) =>
  val === "" || val === null || val === undefined ? undefined : val;

export function calculateCashToCreditConversionRate(
  foodCredit: unknown,
  cashGranted: unknown
): number | undefined {
  const food = typeof foodCredit === "number" ? foodCredit : Number(foodCredit);
  const cash =
    typeof cashGranted === "number" ? cashGranted : Number(cashGranted);

  if (
    !Number.isFinite(food) ||
    !Number.isFinite(cash) ||
    cash <= 0 ||
    food <= 0
  ) {
    return undefined;
  }

  return Math.round((food / cash) * 1e4) / 1e4;
}

export const FundingCreditTermsSchema = z.object({
  cashGranted: z.coerce
    .number("Cash granted is not valid")
    .positive("Cash granted must be greater than zero")
    .max(MAX_CREDIT_AMOUNT, "Maximum allowed amount is 9,999,999")
    .int("Cash granted must be a whole number"),
  requestedAmount: z.coerce
    .number("Food credit is not valid")
    .positive("Food credit must be greater than zero")
    .max(MAX_CREDIT_AMOUNT, "Maximum allowed amount is 9,999,999")
    // Funding is asked for in whole units - the input only accepts digits, so
    // a decimal never reaches here. Stated as a rule rather than left implied,
    // because the field's parseValue strips the dot rather than the digits
    // after it: allow decimals through and 2.75 is read as 275.
    .int("Food credit must be a whole number"),
  cashToCreditConversionRate: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number("Cash to credit conversion rate is not valid")
      .positive("Cash to credit conversion rate must be greater than zero")
      .optional()
  ),
  contractStartDate: z.string().optional().or(z.literal("")),
  contractDurationMonths: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number("Contract duration is not valid")
      .int("Contract duration must be a whole number of months")
      .positive("Contract duration must be a whole positive number")
      .optional()
  ),
});

export const FundingSchema = FundingCreditTermsSchema.extend({
  timezoneOffsetMinutes: z.number().optional(),
  distributionMethod: z.enum(Object.values(DistributionMethod)),
  distributions: z
    .array(
      z.object({
        brandId: z.string(),
        percent: z.coerce
          .number("Invalid number")
          .min(0, "Percent must be ≥ 0")
          .max(100, "Percent must be ≤ 100")
          .refine(val => {
            const [, decimals] = val.toString().split(".");
            return !decimals || decimals.length <= 2;
          }, "Maximum two decimal places allowed"),
      })
    )
    .nullable(),
});

export function calculateCreditEndDate(
  startDate: string | undefined,
  durationMonths: number | null | undefined
): string | undefined {
  if (!startDate || !durationMonths) {
    return undefined;
  }

  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) {
    return undefined;
  }

  return addMonths(start, durationMonths, { in: UTC }).toISOString();
}

export const FundingCreditPeriodSchema = z
  .object({
    startDate: z.string().optional().or(z.literal("")),
    endDate: z.string().optional().or(z.literal("")),
  })
  .superRefine((data, ctx) => {
    if (data.endDate && !data.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Credit start date is required when credit end date is set",
        path: ["startDate"],
      });
      return;
    }

    if (!data.startDate || !data.endDate) {
      return;
    }

    if (
      startOfDay(new Date(data.endDate), { in: UTC }) <
      startOfDay(new Date(data.startDate), { in: UTC })
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Credit end date must not be earlier than credit start date",
        path: ["endDate"],
      });
    }
  });

export type FundingCreditTermsFormType = z.infer<
  typeof FundingCreditTermsSchema
>;
export type FundingFormType = z.infer<typeof FundingSchema>;
export type FundingCreditPeriodFormType = z.infer<
  typeof FundingCreditPeriodSchema
>;
